import { useCallback, useEffect, useMemo } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { pick } from 'lodash';
import { shallow } from 'zustand/shallow';

import {
  CurrencyNode,
  ListItemProps,
  SvgIcon,
} from '../../components/Molecules';
import { LogoutConfirmPopup } from '../../components/Popups';
import { AccountTemplate } from '../../components/Templates/AccountSetting/AccountTemplate';
import { WebPlanDetails } from '../../constants/allowance';
import { StaminaType } from '../../constants/currencies';
import { Icons, IconType } from '../../constants/icons';
import { SVGIconType } from '../../constants/svgIcon';
import {
  getWebPageCustomLink,
  getWebPageLink,
  WebPageLink,
  WebPageType,
} from '../../constants/webPageType';
import { usePopup, useShareData } from '../../context';
import useGameCurrency from '../../hooks/useGameCurrency';
import useRedirectEntry from '../../hooks/useRedirectEntry';
import { BalanceType } from '../../models/Base';
import { useSessionStore } from '../../stores/SessionStore';
import { useUserStore } from '../../stores/UserStore';

import styles from '../../components/Templates/AccountSetting/AccountTemplate/AccountTemplate.module.css';

const { logout } = useSessionStore.getState();

interface AccountInfoScreenProps {
  webPageType: WebPageType;
}

const AccountScreen: React.FC<AccountInfoScreenProps> = ({ webPageType }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { sharedData, setSharedData } = useShareData();
  const { showPopup, hidePopup } = usePopup();

  useEffect(() => {
    setSharedData(undefined);
  }, [setSharedData]);

  useRedirectEntry({ webPageType });

  const userId = useSessionStore((state) => state.activeUserId);
  const account = useUserStore((state) => state.getAccount(userId ?? ''));
  const profile = useUserStore((state) => state.getProfile(userId ?? ''));
  const allowance = useUserStore((store) => store.getAllowance());

  const { isTrialUser } = useUserStore(
    (state) => pick(state.getProfile(userId ?? ''), ['isTrialUser']),
    shallow,
  );

  const onClickAccountInformation = useCallback(() => {
    navigate(
      getWebPageCustomLink(webPageType, '/settings/account/information'),
    );
  }, [navigate, webPageType]);

  const arrowIcon = useMemo(
    () => (
      <SvgIcon
        name={SVGIconType.Arrow}
        color={'#808080'}
        width={15}
        height={15}
      />
    ),
    [],
  );

  const accountListData: ListItemProps[] = useMemo(() => {
    return [
      {
        icon: { resource: { uri: Icons[IconType.ParticipantLine] } },
        title: t('Account Information'),
        description: '',
        rightComponent: arrowIcon,
        onPress: onClickAccountInformation,
      },
    ];
  }, [arrowIcon, onClickAccountInformation, t]);

  const { gameStamina, creationStamina, token } = useGameCurrency(
    account,
    profile,
    allowance,
    false,
  );

  const activeWebPlanTier = account?.activeWebPlanTier ?? 'tier0';
  const storage = WebPlanDetails[activeWebPlanTier].storage;
  const createdItemCount = profile?.createdItemCount ?? 0;

  const storagePercentage = Math.round((createdItemCount / storage) * 100);

  const onClickWallet = useCallback(() => {
    navigate('/wallet');
  }, [navigate]);

  const onClickCloudStorage = useCallback(() => {
    navigate('/cloud-storage');
  }, [navigate]);

  const walletListData: ListItemProps[] = useMemo(
    () => [
      {
        icon: SVGIconType.Wallet,
        title: t('Wallet'),
        description: '',
        rightComponent: (
          <div className={styles.currencyContainer}>
            <CurrencyNode
              currencyType={StaminaType.GameStamina}
              amount={gameStamina}
              withBox={false}
              isPaying={false}
              textStyle={{
                fontSize: '15px',
                fontWeight: 'normal',
                color: 'grey',
              }}
            />
            <CurrencyNode
              currencyType={StaminaType.CreationStamina}
              amount={creationStamina}
              withBox={false}
              isPaying={false}
              textStyle={{
                fontSize: '15px',
                fontWeight: 'normal',
                color: 'grey',
              }}
            />
            <CurrencyNode
              currencyType={BalanceType.Token}
              amount={token}
              withBox={false}
              isPaying={false}
              textStyle={{
                fontSize: '15px',
                fontWeight: 'normal',
                color: 'grey',
              }}
            />
            {/* <CurrencyNode
              currencyType={BalanceType.BreadSlice}
              amount={breadSlice}
              withBox={false}
              isPaying={false}
              textStyle={{
                fontSize: '15px',
                fontWeight: 'normal',
                color: 'grey',
              }}
            /> */}
            {arrowIcon}
          </div>
        ),
        onPress: onClickWallet,
      },
      {
        icon: SVGIconType.Storage,
        title: t('Cloud Storage'),
        description: '',
        rightComponent: (
          <div className={styles.storageContainer}>
            <p className={`${storagePercentage > 100 ? styles.redText : ''}`}>
              {isTrialUser ? '' : `${storagePercentage}%`}
            </p>
            {arrowIcon}
          </div>
        ),
        onPress: onClickCloudStorage,
      },
    ],
    [
      arrowIcon,
      creationStamina,
      gameStamina,
      isTrialUser,
      onClickCloudStorage,
      onClickWallet,
      storagePercentage,
      t,
      token,
    ],
  );

  const onClickLogout = useCallback(async () => {
    const toastId = toast.loading(
      t('Please wait while we are signing you out...'),
    );
    await logout();
    toast.dismiss(toastId);
    if (sharedData && sharedData.searchReferralCode) {
      const pageUrl = window.location.href;

      const updatedUrl = new URL(pageUrl);
      if (sharedData.pathName) {
        updatedUrl.pathname = sharedData.pathName;
      }

      const queryParams = new URLSearchParams();
      if (sharedData.searchReferralCode) {
        queryParams.set('referralCode', sharedData.searchReferralCode);
      }
      updatedUrl.search = queryParams.toString();

      const newUrl =
        updatedUrl.pathname + (updatedUrl.search ? `${updatedUrl.search}` : '');

      navigate(newUrl, { replace: true });
    } else {
      navigate(getWebPageLink(webPageType, WebPageLink.Entry), {
        replace: true,
      });
      setSharedData(undefined);
    }
    // window.location.reload();
  }, [navigate, setSharedData, sharedData, t, webPageType]);

  const confirmLogoutPopup = useCallback(() => {
    showPopup(
      <LogoutConfirmPopup
        onClickLeave={() => {
          onClickLogout().catch(console.error);
          hidePopup();
        }}
        onClickCancel={hidePopup}
      />,
    );
  }, [hidePopup, onClickLogout, showPopup]);

  const onClickTrialLogout = useCallback(() => {
    if (isTrialUser) {
      navigate('/register');
    }
  }, [isTrialUser, navigate]);

  const logoutListData: ListItemProps[] = useMemo(
    () => [
      {
        icon: SVGIconType.Logout,
        title: t('Log Out'),
        description: '',
        rightComponent: arrowIcon,
        onPress: isTrialUser ? onClickTrialLogout : confirmLogoutPopup,
      },
    ],
    [arrowIcon, confirmLogoutPopup, isTrialUser, onClickTrialLogout, t],
  );

  return (
    <AccountTemplate
      profile={profile ?? undefined}
      account={account ?? undefined}
      accountListData={accountListData}
      walletListData={walletListData}
      logoutListData={logoutListData}
    />
  );
};

export default AccountScreen;
